import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckIcon from "@material-ui/icons/Check";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import formatPhone from "../../helpers/formatPhone";
import { formDialogStyles, FormDialogHeader, FormSection } from "../FormDialog";
import SlotPicker from "./SlotPicker";

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const useStyles = makeStyles((theme) => ({
  ...formDialogStyles(theme),
  paper: { borderRadius: 16, overflow: "hidden" },
  body: { display: "flex", [theme.breakpoints.down("sm")]: { flexDirection: "column" } },
  main: { flex: 1, minWidth: 0, overflowY: "auto" },
  side: {
    width: 320,
    flexShrink: 0,
    padding: theme.spacing(3, 2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? alpha("#fff", 0.04) : "#F7F8FA",
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: { width: "auto", borderLeft: 0, borderTop: `1px solid ${theme.palette.divider}` },
  },
  field: { "& .MuiOutlinedInput-root": { borderRadius: 10 } },
  serviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  serviceCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    textAlign: "left",
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    font: "inherit",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  serviceCardActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  serviceName: { fontSize: 14, fontWeight: 600 },
  serviceMeta: { fontSize: 12, color: theme.palette.text.secondary },
  pills: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  pill: {
    height: 36,
    padding: theme.spacing(0, 1.75),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    font: "500 13px Inter, Roboto, sans-serif",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  pillActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    fontWeight: 600,
  },
  linkButton: {
    border: 0,
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    color: theme.palette.primary.main,
    font: "600 13px Inter, Roboto, sans-serif",
  },
  bubbleWrap: { display: "flex", flexDirection: "column", gap: 6 },
  bubble: {
    alignSelf: "flex-end",
    maxWidth: 280,
    padding: theme.spacing(1, 1.25),
    borderRadius: "10px 2px 10px 10px",
    backgroundColor: "#D9FDD3",
    color: "#111B21",
    fontSize: 13,
    lineHeight: "19px",
    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
  },
  sideBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  total: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    fontSize: 14,
  },
}));

const money = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// DDD + número sem o 55 → completa; quem já digitou 55 fica como está.
const normalizeNumber = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith("55")) return `55${digits}`;
  return digits;
};

const NewAppointmentModal = ({ open, onClose, onCreated, initialDate, defaultProviderId }) => {
  const classes = useStyles();

  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Cliente
  const [contact, setContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [newClient, setNewClient] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientNumber, setClientNumber] = useState("");

  const [serviceId, setServiceId] = useState(null);
  const [providerId, setProviderId] = useState(""); // "" = qualquer disponível
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [sendReminder, setSendReminder] = useState(true);
  const [reminderHours, setReminderHours] = useState(24);
  const [queueId, setQueueId] = useState("");
  const [saving, setSaving] = useState(false);

  // Zera o formulário a cada abertura.
  useEffect(() => {
    if (!open) return;
    setContact(null);
    setContactOptions([]);
    setContactSearch("");
    setNewClient(false);
    setClientName("");
    setClientNumber("");
    setServiceId(null);
    setProviderId(defaultProviderId ? String(defaultProviderId) : "");
    setDate(initialDate || moment().format("YYYY-MM-DD"));
    setSlot(null);
    setNotes("");
    setSendReminder(true);
    setReminderHours(24);
    setQueueId("");
  }, [open, initialDate, defaultProviderId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingData(true);
    Promise.all([
      api.get("/services", { params: { pageNumber: 1 } }),
      api.get("/users/list"),
      api.get("/queue"),
    ])
      .then(([{ data: s }, { data: u }, { data: q }]) => {
        if (cancelled) return;
        setServices((s.services || []).filter((x) => x.active !== false));
        const list = Array.isArray(u) ? u : u?.users || [];
        setProviders(list.filter((x) => x.isServiceProvider));
        setQueues(Array.isArray(q) ? q : []);
      })
      .catch(toastError)
      .finally(() => !cancelled && setLoadingData(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Busca de contatos
  useEffect(() => {
    if (!open || contactSearch.trim().length < 2) {
      setContactOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { searchParam: contactSearch.trim() } });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [contactSearch, open]);

  const service = useMemo(() => services.find((s) => s.id === serviceId) || null, [services, serviceId]);

  // Profissionais que atendem o serviço escolhido (vínculo serviço↔usuário).
  const serviceProviders = useMemo(() => {
    if (!service) return [];
    const ids = new Set((service.users || []).map((u) => u.id));
    return providers.filter((p) => ids.has(p.id));
  }, [service, providers]);

  // Trocar de serviço pode invalidar o profissional e o horário escolhidos.
  useEffect(() => {
    setSlot(null);
    if (providerId && service && !serviceProviders.some((p) => String(p.id) === String(providerId))) {
      setProviderId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  // Mudou o profissional ou o dia: o horário escolhido deixa de valer.
  useEffect(() => {
    setSlot(null);
  }, [providerId, date]);

  const chosenProvider = useMemo(() => {
    const id = slot?.userId ?? providerId;
    return providers.find((p) => String(p.id) === String(id)) || null;
  }, [slot, providerId, providers]);

  const start = slot ? moment(slot.startAt) : null;
  const clientOk = newClient ? clientName.trim().length >= 2 && normalizeNumber(clientNumber).length >= 10 : !!contact;
  const canSubmit = clientOk && !!service && !!slot && !saving;

  const firstName = (newClient ? clientName : contact?.name || "").trim();
  const preview = start
    ? `Olá ${firstName || "{nome}"}! Seu agendamento de ${service.name} foi confirmado para ${start.format(
        "DD/MM/YYYY [às] HH:mm"
      )}. Qualquer dúvida, é só responder por aqui. 😊`
    : null;

  const reminderAt = start ? start.clone().subtract(reminderHours, "hours") : null;
  const reminderOk = reminderAt && reminderAt.isAfter(moment());

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/appointments", {
        serviceId: service.id,
        userId: slot.userId ?? providerId,
        startAt: slot.startAt,
        contactName: newClient ? clientName.trim() : contact.name,
        contactNumber: newClient ? normalizeNumber(clientNumber) : contact.number,
        notes: notes.trim() || undefined,
        createReminder: sendReminder,
        reminderHoursBefore: reminderHours,
        scheduleQueueId: queueId || undefined,
      });
      toast.success("Agendamento criado.");
      onCreated && onCreated(data);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth classes={{ paper: classes.paper }}>
      <FormDialogHeader
        title="Novo agendamento"
        subtitle="Só aparecem horários livres do profissional, já descontando bloqueios e outros agendamentos."
        onClose={onClose}
        disabled={saving}
      />
      <DialogContent dividers style={{ padding: 0 }}>
        <div className={classes.body}>
          <div className={classes.main}>
            <div className={classes.content}>
              <FormSection
                title="Cliente"
                action={
                  <button type="button" className={classes.linkButton} onClick={() => setNewClient((v) => !v)}>
                    {newClient ? "Buscar cliente existente" : "Novo cliente"}
                  </button>
                }
              >
                {newClient ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <TextField
                      className={classes.field}
                      style={{ flex: "1 1 200px" }}
                      label="Nome"
                      variant="outlined"
                      size="small"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                    />
                    <TextField
                      className={classes.field}
                      style={{ flex: "1 1 200px" }}
                      label="WhatsApp com DDD"
                      variant="outlined"
                      size="small"
                      value={clientNumber}
                      onChange={(e) => setClientNumber(e.target.value)}
                      placeholder="(41) 99999-9999"
                    />
                  </div>
                ) : (
                  <Autocomplete
                    value={contact}
                    options={contactOptions}
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => o.name || ""}
                    getOptionSelected={(o, v) => o.id === v.id}
                    noOptionsText={contactSearch.trim().length < 2 ? "Digite ao menos 2 letras" : "Nenhum contato encontrado"}
                    onChange={(_, v) => setContact(v)}
                    onInputChange={(_, v) => setContactSearch(v)}
                    renderOption={(o) => (
                      <div>
                        <div style={{ fontWeight: 600 }}>{o.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{formatPhone(o.number)}</div>
                      </div>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={classes.field}
                        variant="outlined"
                        size="small"
                        placeholder="Buscar por nome ou telefone"
                      />
                    )}
                  />
                )}
              </FormSection>

              <FormSection title="Serviço">
                {loadingData ? (
                  <CircularProgress size={22} />
                ) : services.length === 0 ? (
                  <Typography variant="body2" className={classes.muted}>
                    Nenhum serviço ativo. Cadastre em Agenda › Serviços.
                  </Typography>
                ) : (
                  <div className={classes.serviceGrid}>
                    {services.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={s.id === serviceId}
                        className={`${classes.serviceCard} ${s.id === serviceId ? classes.serviceCardActive : ""}`}
                        onClick={() => setServiceId(s.id)}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span className={classes.serviceName} style={{ display: "block" }}>{s.name}</span>
                          <span className={classes.serviceMeta}>{s.durationMinutes} min</span>
                        </span>
                        <strong style={{ fontSize: 13 }}>{money(s.price)}</strong>
                      </button>
                    ))}
                  </div>
                )}
              </FormSection>

              {service && (
                <FormSection title="Profissional">
                  {serviceProviders.length === 0 ? (
                    <Typography variant="body2" className={classes.muted}>
                      Nenhum profissional atende este serviço. Vincule em Agenda › Serviços.
                    </Typography>
                  ) : (
                    <div className={classes.pills}>
                      <button
                        type="button"
                        aria-pressed={providerId === ""}
                        className={`${classes.pill} ${providerId === "" ? classes.pillActive : ""}`}
                        onClick={() => setProviderId("")}
                      >
                        Qualquer disponível
                      </button>
                      {serviceProviders.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          aria-pressed={String(providerId) === String(p.id)}
                          className={`${classes.pill} ${String(providerId) === String(p.id) ? classes.pillActive : ""}`}
                          onClick={() => setProviderId(String(p.id))}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </FormSection>
              )}

              {service && serviceProviders.length > 0 && (
                <FormSection title="Data e horário">
                  <SlotPicker
                    serviceId={service.id}
                    userId={providerId}
                    date={date}
                    onDateChange={setDate}
                    value={slot}
                    onChange={setSlot}
                  />
                  {start && (
                    <Typography variant="body2">
                      <strong>
                        {WEEKDAYS[start.day()]}, {start.format("DD/MM")} · {start.format("HH:mm")} às{" "}
                        {moment(slot.endAt).format("HH:mm")}
                      </strong>
                      {chosenProvider ? ` com ${chosenProvider.name}` : ""}
                    </Typography>
                  )}
                </FormSection>
              )}

              <FormSection title="Observações (opcional, só a equipe vê)">
                <TextField
                  className={classes.field}
                  multiline
                  minRows={2}
                  variant="outlined"
                  size="small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormSection>
            </div>
          </div>

          <aside className={classes.side}>
            <Typography className={classes.sectionTitle}>Mensagens no WhatsApp</Typography>
            <div className={classes.bubbleWrap}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Confirmação agora
              </Typography>
              {preview ? (
                <div className={classes.bubble}>{preview}</div>
              ) : (
                <Typography variant="body2" className={classes.muted}>
                  Escolha serviço e horário para ver a mensagem.
                </Typography>
              )}
            </div>

            <div className={classes.sideBlock}>
              <FormControlLabel
                control={
                  <Checkbox color="primary" checked={sendReminder} onChange={(e) => setSendReminder(e.target.checked)} />
                }
                label={<span style={{ fontSize: 14, fontWeight: 600 }}>Enviar lembrete</span>}
              />
              {sendReminder && (
                <>
                  <TextField
                    select
                    className={classes.field}
                    variant="outlined"
                    size="small"
                    value={reminderHours}
                    onChange={(e) => setReminderHours(Number(e.target.value))}
                  >
                    <MenuItem value={24}>24 horas antes</MenuItem>
                    <MenuItem value={3}>3 horas antes</MenuItem>
                    <MenuItem value={1}>1 hora antes</MenuItem>
                  </TextField>
                  {reminderAt && (
                    <Typography variant="body2" className={classes.muted}>
                      {reminderOk
                        ? `Lembrete em ${WEEKDAYS[reminderAt.day()]}, ${reminderAt.format("DD/MM [às] HH:mm")}`
                        : "Horário muito próximo: este lembrete não será enviado."}
                    </Typography>
                  )}
                </>
              )}
            </div>

            <div className={classes.sideBlock}>
              <TextField
                select
                className={classes.field}
                variant="outlined"
                size="small"
                label="Se o cliente responder, abrir atendimento no departamento"
                value={queueId}
                onChange={(e) => setQueueId(e.target.value)}
              >
                <MenuItem value="">Nenhuma</MenuItem>
                {queues.map((q) => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div className={classes.total}>
              <span className={classes.muted}>Valor do serviço</span>
              <strong>{money(service?.price)}</strong>
            </div>
          </aside>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button className={`${classes.actionButton} ${classes.cancelButton}`} onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          className={classes.actionButton}
          disabled={!canSubmit}
          startIcon={saving ? null : <CheckIcon />}
          onClick={submit}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : start ? (
            `Agendar ${WEEKDAYS[start.day()]}, ${start.format("DD/MM [às] HH:mm")}`
          ) : (
            "Agendar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewAppointmentModal;
